import { useRef, useState, type ReactNode } from "react";
import {
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Circle,
  FileUp,
  Loader2,
} from "lucide-react";

import { cn } from "@/lib/utils";

/**
 * One console row (F1 Donesi konzola): one item = one place for input,
 * status and evidence.
 *
 * - With ``onFiles`` the whole row is a PDF drop-zone (drag or click) —
 *   the targeted-upload path that carries an ``expected_type`` hint.
 * - With ``children`` the row expands to its evidence (document card,
 *   parsed situacija figures) or an inline mini-entry.
 * - ``notice`` renders ALWAYS-visible under the header — conflict and
 *   error messages must never hide inside a collapsed expand.
 */
export function KonzolaRow({
  label,
  present,
  optional,
  summary,
  notice,
  onFiles,
  uploading,
  children,
  defaultOpen,
}: {
  label: string;
  present: boolean;
  /** Not counted as missing; renders the "po potrebi" tag when empty. */
  optional?: boolean;
  /** Short evidence/value summary next to the label (file name, values…). */
  summary?: ReactNode;
  /** Always-visible message under the header (mismatch, upload error). */
  notice?: ReactNode;
  /** Makes the row a PDF drop-zone; receives the dropped/picked files. */
  onFiles?: (files: File[]) => void;
  uploading?: boolean;
  /** Evidence / mini-entry the row expands to. */
  children?: ReactNode;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen ?? false);
  const [dragOver, setDragOver] = useState(false);
  const [rejected, setRejected] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const expandable = children != null;

  const takeFiles = (all: File[]) => {
    const pdfs = all.filter((f) => f.name.toLowerCase().endsWith(".pdf"));
    const other = all.filter((f) => !f.name.toLowerCase().endsWith(".pdf"));
    setRejected(
      other.length
        ? `${other.map((f) => f.name).join(", ")}: na redak idu PDF-ovi — nacrt u DXF/IFC formatu ubaci u koš ispod.`
        : null,
    );
    if (pdfs.length) onFiles?.(pdfs);
  };

  const openPicker = () => inputRef.current?.click();
  const headerClick = () => {
    if (expandable) setOpen((o) => !o);
    else if (onFiles) openPicker();
  };

  return (
    <li
      className={cn(
        "rounded-md border transition-colors",
        dragOver
          ? "border-ind-500 bg-ind-50"
          : "border-transparent hover:bg-stone-50",
      )}
      onDragOver={
        onFiles
          ? (e) => {
              e.preventDefault();
              setDragOver(true);
            }
          : undefined
      }
      onDragLeave={onFiles ? () => setDragOver(false) : undefined}
      onDrop={
        onFiles
          ? (e) => {
              e.preventDefault();
              setDragOver(false);
              takeFiles(Array.from(e.dataTransfer.files));
            }
          : undefined
      }
    >
      {onFiles && (
        <input
          ref={inputRef}
          type="file"
          accept=".pdf"
          multiple
          className="hidden"
          onChange={(e) => {
            takeFiles(Array.from(e.target.files ?? []));
            e.target.value = "";
          }}
        />
      )}

      <div
        className={cn(
          "flex min-h-[2.1rem] items-center gap-2 px-2 py-1",
          (expandable || onFiles) && "cursor-pointer",
        )}
        onClick={headerClick}
        role={expandable || onFiles ? "button" : undefined}
      >
        {uploading ? (
          <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-ind-600" />
        ) : present ? (
          <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-green-600" />
        ) : (
          <Circle
            className={cn(
              "h-3.5 w-3.5 shrink-0",
              optional ? "text-stone-300" : "text-amber-400",
            )}
          />
        )}

        <span
          className={cn(
            "text-[0.84rem]",
            present ? "font-medium text-stone-800" : "text-stone-600",
          )}
        >
          {label}
          {optional && !present && (
            <span className="text-stone-300"> · po potrebi</span>
          )}
        </span>

        {summary && (
          <span className="min-w-0 flex-1 truncate text-[0.78rem] text-stone-500">
            {summary}
          </span>
        )}
        {!summary && <span className="flex-1" />}

        {onFiles &&
          (present ? (
            <button
              type="button"
              className="inline-flex shrink-0 items-center gap-1 rounded px-1.5 py-0.5 text-[0.72rem] font-semibold text-ind-700 hover:bg-ind-50 hover:text-ind-800"
              onClick={(e) => {
                e.stopPropagation();
                openPicker();
              }}
            >
              <FileUp className="h-3 w-3" /> Dodaj PDF
            </button>
          ) : (
            <span
              className="inline-flex shrink-0 items-center gap-1 text-[0.74rem] text-ind-600"
              onClick={
                // The empty row's whole surface opens the picker only when
                // there is nothing to expand — otherwise this affordance does.
                expandable
                  ? (e) => {
                      e.stopPropagation();
                      openPicker();
                    }
                  : undefined
              }
            >
              <FileUp className="h-3 w-3" />
              povuci PDF ovamo ili klikni
            </span>
          ))}

        {!present && !optional && !uploading && (
          <span className="shrink-0 text-[0.74rem] font-semibold text-amber-700">
            — fali
          </span>
        )}

        {expandable &&
          (open ? (
            <ChevronDown className="h-3.5 w-3.5 shrink-0 text-stone-400" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5 shrink-0 text-stone-400" />
          ))}
      </div>

      {rejected && (
        <p className="mx-2 mb-1.5 rounded bg-amber-50 px-2 py-1 text-[0.76rem] text-amber-800">
          {rejected}
        </p>
      )}
      {notice}
      {expandable && open && (
        <div className="mx-2 mb-2 mt-0.5 border-l-2 border-stone-200 pl-3">
          {children}
        </div>
      )}
    </li>
  );
}
